import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Loader2, ArrowLeft } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogle = () => {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Register() {
  const [mode, setMode] = useState("create");
  const [form, setForm] = useState({ name: "", org_name: "", email: "", password: "", join_code: "" });
  const [loading, setLoading] = useState(false);
  const [codeInfo, setCodeInfo] = useState(null);
  const [codeSource, setCodeSource] = useState("manual");
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const c = (searchParams.get("code") || "").trim().toUpperCase();
    if (c) { setMode("join"); setForm((f) => ({ ...f, join_code: c })); setCodeSource(searchParams.get("src") === "qr" ? "qr" : "link"); }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (mode !== "join") { setCodeInfo(null); return undefined; }
    const c = form.join_code.trim().toUpperCase();
    if (c.length < 6) { setCodeInfo(null); return undefined; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get(`/join-code/${c}`)
        .then(({ data }) => { if (!cancelled) setCodeInfo(data); })
        .catch(() => { if (!cancelled) setCodeInfo(null); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.join_code, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = mode === "join"
        ? { name: form.name, email: form.email, password: form.password, join_code: form.join_code.trim().toUpperCase(), join_source: codeSource }
        : { name: form.name, email: form.email, password: form.password, org_name: form.org_name };
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      toast.success(mode === "join" ? "You've joined the company! Welcome." : "Agency created! Welcome to IShareLeads.");
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between bg-[#0B0F19] p-12 text-white lg:flex grid-bg">
        <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold">
          <Shield className="h-6 w-6 text-primary" /> IShareLeads
        </Link>
        <div>
          <h2 className="font-heading text-4xl font-extrabold leading-tight">Launch your agency in minutes.</h2>
          <p className="mt-4 max-w-md text-slate-400">Create your workspace, invite your team, and start capturing leads across every line of coverage.</p>
        </div>
        <p className="font-mono text-xs text-slate-600">© 2026 IShareLeads</p>
      </div>

      <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-1/2 lg:px-24">
        <Link to="/" className="mb-8 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-home-link">
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>
        <h1 className="font-heading text-3xl font-bold">{mode === "join" ? "Join a company" : "Create your agency"}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{mode === "join" ? "Enter your company's join code to join their workspace." : "You'll be the admin of a brand-new workspace."}</p>

        <div className="mt-6 flex rounded-lg border border-border p-1" data-testid="register-mode-toggle">
          <button type="button" onClick={() => setMode("create")} data-testid="mode-create" className={`flex-1 rounded-md py-1.5 text-sm font-medium transition-colors ${mode === "create" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Create a company</button>
          <button type="button" onClick={() => setMode("join")} data-testid="mode-join" className={`flex-1 rounded-md py-1.5 text-sm font-medium transition-colors ${mode === "join" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Join with a code</button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="name">Your name</Label>
              <Input id="name" value={form.name} required data-testid="register-name-input" onChange={set("name")} placeholder="Jane Doe" className="mt-1.5" />
            </div>
            {mode === "create" ? (
              <div>
                <Label htmlFor="org">Agency name</Label>
                <Input id="org" value={form.org_name} required data-testid="register-org-input" onChange={set("org_name")} placeholder="Acme Insurance" className="mt-1.5" />
              </div>
            ) : (
              <div>
                <Label htmlFor="code">Company join code</Label>
                <Input id="code" value={form.join_code} required data-testid="register-joincode-input" onChange={(e) => { setForm({ ...form, join_code: e.target.value }); setCodeSource("manual"); }} placeholder="ABC123" className="mt-1.5 font-mono uppercase tracking-widest" />
              </div>
            )}
          </div>
          {mode === "join" && codeInfo && (
            <div data-testid="joincode-info" className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
              Joining <strong>{codeInfo.org_name}</strong> as {codeInfo.role === "manager" ? "a" : "an"} <strong>{codeInfo.role === "manager" ? "Manager" : "Agent"}</strong>.
              {codeInfo.requires_approval && <span className="mt-1 block text-xs text-muted-foreground">An admin will approve your access before you can see leads.</span>}
            </div>
          )}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={form.email} required data-testid="register-email-input" onChange={set("email")} placeholder="you@agency.com" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={form.password} required data-testid="register-password-input" onChange={set("password")} placeholder="Min. 6 characters" className="mt-1.5" />
          </div>
          <Button type="submit" disabled={loading} data-testid="register-submit-button" className="w-full">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {mode === "join" ? "Join company" : "Create agency"}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
          <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
        </div>

        <Button variant="outline" onClick={startGoogle} data-testid="google-register-button" className="w-full">
          <img src="https://www.google.com/favicon.ico" alt="" className="mr-2 h-4 w-4" /> Continue with Google
        </Button>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-primary hover:underline" data-testid="go-login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
